/**
 * HX Operations Center — HX Assistant (RAG).
 *
 * - Multi-turn chat with session persistence (backend-side).
 * - Upload panel for PDF/DOCX/XLSX/PPTX (drag & drop or picker).
 * - Answers cite source documents inline with [S1], [S2], … tokens.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Upload, Send, FileText, Loader2, X, CheckCircle2,
  AlertCircle, ChevronDown, ExternalLink, Trash2,
} from "lucide-react";
import axios from "axios";

const P = {
  bg:      "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E4E7EB",
  dim:     "rgba(228,231,235,0.62)",
  accent:  "#3B82F6",
  accent2: "#22C55E",
  silver:  "#C0C7CE",
  amber:   "#F59E0B",
};

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

// Ordered so the UI select stays consistent with backend supplier IDs.
const SUPPLIER_OPTIONS = [
  { id: "",           label: "— No supplier —" },
  { id: "siemens",    label: "Siemens" },
  { id: "vcow",       label: "VCOW" },
  { id: "dfoptix",    label: "DFOPTIX" },
  { id: "bluesun",    label: "Bluesun Solar" },
  { id: "wattzing",   label: "Wattzing" },
  { id: "beny",       label: "BENY New Energy" },
  { id: "neura",      label: "NEURA Robotics" },
  { id: "fraunhofer", label: "Fraunhofer" },
];

const LANG_OPTIONS = [
  { id: "auto", label: "Auto-detect" },
  { id: "pt",   label: "Português" },
  { id: "en",   label: "English" },
  { id: "de",   label: "Deutsch" },
];

const CATEGORY_OPTIONS = [
  "Datasheet", "Certification", "Catalog", "Manual",
  "Contract", "Proposal", "Presentation", "Other",
];

// Convert [S1], [S2] markers in the reply into clickable pills that scroll
// to the corresponding citation entry.
const renderReplyWithMarkers = (text, citations, onCite) => {
  if (!text) return null;
  const parts = text.split(/(\[S\d+\])/g);
  return parts.map((chunk, i) => {
    const m = chunk.match(/^\[S(\d+)\]$/);
    if (m) {
      const idx = parseInt(m[1], 10) - 1;
      const c = citations[idx];
      if (!c) return <span key={i}>{chunk}</span>;
      return (
        <button
          key={i}
          onClick={() => onCite(idx)}
          data-testid={`assistant-cite-${idx + 1}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-[10px] font-mono uppercase tracking-wider align-middle transition hover:brightness-125"
          style={{ background: `${P.accent}22`, color: P.accent, border: `1px solid ${P.accent}55` }}
          title={c.document_title + (c.page ? ` · p.${c.page}` : "")}
        >
          S{idx + 1}
        </button>
      );
    }
    return <span key={i}>{chunk}</span>;
  });
};

const UploadPanel = ({ onUploaded, onClose }) => {
  const [file, setFile] = React.useState(null);
  const [supplier, setSupplier] = React.useState("");
  const [category, setCategory] = React.useState("Datasheet");
  const [title, setTitle] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [drag, setDrag] = React.useState(false);

  const doUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    if (supplier) fd.append("supplier_id", supplier);
    if (category) fd.append("category", category);
    if (title) fd.append("title", title);
    try {
      const r = await axios.post(`${API}/ops/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      setResult(r.data);
      onUploaded?.(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) {
      setFile(f);
      if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: "rgba(10,15,26,0.92)", backdropFilter: "blur(18px)" }}
      onClick={onClose}
      data-testid="assistant-upload-modal"
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-lg overflow-hidden"
        style={{ background: P.bg, border: `1px solid ${P.border}` }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: P.border }}>
          <div className="flex items-center gap-2">
            <Upload size={15} style={{ color: P.accent }} />
            <div className="text-[13px] font-semibold" style={{ color: "#fff" }}>Upload document</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/[0.08]" style={{ color: P.silver }} data-testid="assistant-upload-close">
            <X size={14} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <label
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={handleDrop}
            data-testid="assistant-upload-dropzone"
            className="block rounded-md p-6 cursor-pointer text-center transition"
            style={{
              background: drag ? `${P.accent}18` : "rgba(255,255,255,0.02)",
              border: `1px dashed ${drag ? P.accent : P.border}`,
            }}
          >
            <input
              type="file"
              accept=".pdf,.docx,.xlsx,.pptx,.txt,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setFile(f);
                  if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
                }
              }}
              data-testid="assistant-upload-input"
            />
            <Upload size={22} className="mx-auto mb-2" style={{ color: P.accent }} />
            <div className="text-[13px] font-semibold" style={{ color: "#fff" }}>
              {file ? file.name : "Drop file here or click to browse"}
            </div>
            <div className="text-[10.5px] mt-1" style={{ color: P.dim }}>
              PDF · DOCX · XLSX · PPTX · TXT — max 40 MB
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.24em] mb-1.5" style={{ color: P.silver }}>Supplier</div>
              <select
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                className="w-full px-3 py-2 text-[12px] rounded-md outline-none"
                style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
                data-testid="assistant-upload-supplier"
              >
                {SUPPLIER_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.24em] mb-1.5" style={{ color: P.silver }}>Category</div>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 text-[12px] rounded-md outline-none"
                style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
                data-testid="assistant-upload-category"
              >
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.24em] mb-1.5" style={{ color: P.silver }}>Title (optional)</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Displayed name for this document"
              className="w-full px-3 py-2 text-[12px] rounded-md outline-none"
              style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
              data-testid="assistant-upload-title"
            />
          </div>

          {uploading && (
            <div className="rounded-md px-3 py-2" style={{ background: `${P.accent}12`, border: `1px solid ${P.accent}55` }}>
              <div className="flex items-center gap-2 text-[12px]" style={{ color: P.accent }}>
                <Loader2 size={12} className="animate-spin" />
                Uploading &amp; indexing… {progress}%
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md px-3 py-2 text-[12px]" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.4)", color: "#FCA5A5" }} data-testid="assistant-upload-error">
              <AlertCircle size={12} className="inline mr-1.5" /> {error}
            </div>
          )}

          {result && (
            <div className="rounded-md px-3 py-2 text-[12px]" style={{ background: `${P.accent2}12`, border: `1px solid ${P.accent2}55`, color: P.accent2 }} data-testid="assistant-upload-success">
              <CheckCircle2 size={12} className="inline mr-1.5" />
              {result.duplicate_of && result.duplicate_of !== result.id
                ? "Document already indexed (duplicate detected)"
                : `Indexed · ${result.pages} pages · ${result.chunks} chunks · ${result.language?.toUpperCase() || "auto"}`}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onClose} data-testid="assistant-upload-cancel"
              className="px-4 py-2 text-[12px] rounded-md" style={{ color: P.silver, border: `1px solid ${P.border}` }}>Close</button>
            <button
              onClick={doUpload}
              disabled={!file || uploading}
              data-testid="assistant-upload-submit"
              className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold rounded-md transition disabled:opacity-40"
              style={{ background: P.accent, color: "#fff" }}
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Upload &amp; index
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CitationCard = React.forwardRef(({ index, citation }, ref) => (
  <div
    ref={ref}
    data-testid={`assistant-citation-${index + 1}`}
    className="rounded-md px-3 py-2.5"
    style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}` }}
  >
    <div className="flex items-start gap-2">
      <span
        className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded shrink-0"
        style={{ background: `${P.accent}22`, color: P.accent, border: `1px solid ${P.accent}55` }}
      >
        S{index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-semibold truncate" style={{ color: "#fff" }}>{citation.document_title}</div>
        <div className="text-[10.5px] mt-0.5" style={{ color: P.dim }}>
          {citation.supplier_id && <span className="uppercase tracking-wider">{citation.supplier_id}</span>}
          {citation.page && <span> · p. {citation.page}</span>}
          <span> · relevance {(citation.score * 100).toFixed(0)}%</span>
        </div>
      </div>
      <a
        href={`${API}/ops/documents/${citation.document_id}/download`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-7 h-7 rounded flex items-center justify-center shrink-0 hover:bg-white/[0.06]"
        style={{ color: P.silver }}
        title="Open source document"
        data-testid={`assistant-citation-open-${index + 1}`}
      >
        <ExternalLink size={11} />
      </a>
    </div>
  </div>
));
CitationCard.displayName = "CitationCard";

const MessageBubble = ({ msg, onCite }) => {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid="assistant-message-user">
        <div
          className="max-w-[80%] rounded-lg px-4 py-2.5 text-[13px]"
          style={{ background: `${P.accent}18`, border: `1px solid ${P.accent}44`, color: "#fff" }}
        >
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start" data-testid="assistant-message-assistant">
      <div className="max-w-[92%] w-full">
        <div className="flex items-center gap-2 mb-1.5 text-[10px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>
          <Sparkles size={10} /> BS Assistente · {msg.lang?.toUpperCase() || "AUTO"}
        </div>
        <div
          className="rounded-lg px-4 py-3 text-[13px] leading-relaxed whitespace-pre-wrap"
          style={{ background: P.surface, border: `1px solid ${P.border}`, color: P.ink }}
        >
          {renderReplyWithMarkers(msg.content, msg.citations || [], onCite)}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------

export const AiAssistantPanel = ({ supplierId }) => {
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [lang, setLang] = React.useState("auto");
  const [sessionId, setSessionId] = React.useState(null);
  const [thinking, setThinking] = React.useState(false);
  const [showUpload, setShowUpload] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [documents, setDocuments] = React.useState([]);
  const [showLibrary, setShowLibrary] = React.useState(false);
  const scrollRef = React.useRef(null);
  const citationRefs = React.useRef({});

  const loadDocuments = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ops/documents`, {
        params: supplierId ? { supplier_id: supplierId } : {},
      });
      setDocuments(r.data || []);
    } catch (e) {
      // best-effort
    }
  }, [supplierId]);

  React.useEffect(() => { loadDocuments(); }, [loadDocuments]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    setError(null);
    setInput("");
    const userMsg = { role: "user", content: text };
    setMessages((m) => [...m, userMsg]);
    setThinking(true);
    try {
      const payload = {
        message: text,
        session_id: sessionId,
        supplier_id: supplierId || undefined,
        top_k: 6,
      };
      if (lang !== "auto") payload.lang = lang;
      const r = await axios.post(`${API}/ops/chat`, payload);
      const data = r.data;
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.reply,
          lang: data.lang,
          citations: data.citations || [],
        },
      ]);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Assistant failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setThinking(false);
    }
  };

  const scrollToCitation = (idx) => {
    const el = citationRefs.current[idx];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.transition = "background 400ms ease";
      const original = el.style.background;
      el.style.background = `${P.accent}22`;
      setTimeout(() => { el.style.background = original || "rgba(255,255,255,0.02)"; }, 900);
    }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const currentCitations = lastAssistant?.citations || [];

  const resetSession = () => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  };

  return (
    <div data-testid="assistant-panel">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.accent }}>BS Assistente · RAG</div>
        <div className="mt-2 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-bold tracking-tight text-[32px] lg:text-[40px] leading-tight" style={{ color: "#fff" }}>
              Second brain.
            </h1>
            <p className="mt-2 text-[13.5px] max-w-2xl" style={{ color: P.dim }}>
              Ask anything about supplier catalogues, certifications and technical sheets. Answers cite the exact page — in PT, DE or EN.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="px-3 py-2 text-[11.5px] rounded-md outline-none"
              style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
              data-testid="assistant-lang-select"
            >
              {LANG_OPTIONS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
            <button
              onClick={() => setShowUpload(true)}
              data-testid="assistant-upload-open"
              className="inline-flex items-center gap-2 px-3 py-2 text-[11.5px] font-semibold rounded-md transition"
              style={{ background: P.accent, color: "#fff" }}
            >
              <Upload size={12} /> Upload document
            </button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        {/* Chat column */}
        <div
          className="rounded-lg overflow-hidden flex flex-col"
          style={{ background: P.surface, border: `1px solid ${P.border}`, minHeight: 520 }}
        >
          <div ref={scrollRef} className="flex-1 p-5 overflow-y-auto space-y-4" style={{ maxHeight: 560 }} data-testid="assistant-scroll">
            {messages.length === 0 && !thinking && (
              <div className="h-full flex flex-col items-center justify-center text-center py-8">
                <Sparkles size={22} style={{ color: P.accent }} />
                <div className="mt-3 text-[13px] font-semibold" style={{ color: "#fff" }}>Ready when you are.</div>
                <div className="mt-1 text-[12px] max-w-md" style={{ color: P.dim }}>
                  Try asking &ldquo;Qual é a capacidade da bateria BENY?&rdquo; or &ldquo;Welche Zertifizierungen hat Bluesun?&rdquo;
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={i} msg={m} onCite={scrollToCitation} />
            ))}
            {thinking && (
              <div className="flex items-center gap-2 text-[11px]" style={{ color: P.silver }} data-testid="assistant-thinking">
                <Loader2 size={11} className="animate-spin" />
                Retrieving &amp; drafting…
              </div>
            )}
            {error && (
              <div className="rounded-md px-3 py-2 text-[12px]"
                style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.4)", color: "#FCA5A5" }}
                data-testid="assistant-error">
                <AlertCircle size={12} className="inline mr-1.5" /> {error}
              </div>
            )}
          </div>
          <div className="p-3 border-t flex items-center gap-2" style={{ borderColor: P.border, background: "rgba(255,255,255,0.015)" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about a supplier, spec, certification, product…"
              disabled={thinking}
              className="flex-1 px-3 py-2.5 text-[13px] rounded-md outline-none"
              style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
              data-testid="assistant-input"
            />
            {messages.length > 0 && (
              <button
                onClick={resetSession}
                title="New session"
                data-testid="assistant-reset"
                className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-white/[0.05]"
                style={{ color: P.silver, border: `1px solid ${P.border}` }}
              >
                <Trash2 size={13} />
              </button>
            )}
            <button
              onClick={send}
              disabled={thinking || !input.trim()}
              data-testid="assistant-send"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] font-semibold rounded-md disabled:opacity-40"
              style={{ background: P.accent, color: "#fff" }}
            >
              {thinking ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Send
            </button>
          </div>
        </div>

        {/* Right rail: citations + library */}
        <div className="space-y-4">
          <div className="rounded-lg p-4" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-3" style={{ color: P.accent }}>
              Sources
              {currentCitations.length > 0 && <span className="ml-2 text-[10px] font-normal" style={{ color: P.silver }}>· {currentCitations.length}</span>}
            </div>
            {currentCitations.length === 0 ? (
              <div className="text-[11.5px]" style={{ color: P.dim }}>
                Ask a question — cited sources will appear here with page numbers and links.
              </div>
            ) : (
              <div className="space-y-2">
                {currentCitations.map((c, i) => (
                  <CitationCard
                    key={i}
                    index={i}
                    citation={c}
                    ref={(el) => { if (el) citationRefs.current[i] = el; }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
            <button
              onClick={() => setShowLibrary((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03]"
              data-testid="assistant-library-toggle"
            >
              <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.accent }}>
                Indexed library · {documents.length}
              </span>
              <ChevronDown size={13} style={{ color: P.silver, transform: showLibrary ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
            </button>
            {showLibrary && (
              <div className="px-4 pb-4 max-h-72 overflow-y-auto space-y-1.5">
                {documents.length === 0 ? (
                  <div className="text-[11.5px]" style={{ color: P.dim }}>
                    No documents indexed yet. Use &ldquo;Upload document&rdquo; to start.
                  </div>
                ) : documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 py-1.5" data-testid={`assistant-library-item-${d.id}`}>
                    <FileText size={11} style={{ color: P.silver }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11.5px] truncate" style={{ color: "#fff" }}>{d.title || d.original_filename}</div>
                      <div className="text-[9.5px]" style={{ color: P.dim }}>
                        {d.supplier_id || "unassigned"} · {d.pages}p · {d.chunks}c · {d.language?.toUpperCase() || "?"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showUpload && (
          <UploadPanel
            onUploaded={() => { loadDocuments(); }}
            onClose={() => setShowUpload(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AiAssistantPanel;
